import { OTPInput, OTPInputContext, type OTPInputProps, type SlotProps } from 'input-otp';
import { forwardRef, useContext, type HTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';

export type InputOTPProps = OTPInputProps;

const InputOTP = forwardRef<HTMLInputElement, InputOTPProps>(
  ({ className, containerClassName, ...props }, ref) => (
    <OTPInput
      ref={ref}
      data-slot="input-otp"
      containerClassName={cn('flex items-center has-disabled:opacity-50', containerClassName)}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  )
);
InputOTP.displayName = 'InputOTP';

export interface InputOTPGroupProps extends HTMLAttributes<HTMLDivElement> {}

const InputOTPGroup = forwardRef<HTMLDivElement, InputOTPGroupProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="input-otp-group"
      className={cn('flex items-center', className)}
      {...props}
    />
  )
);
InputOTPGroup.displayName = 'InputOTPGroup';

export interface InputOTPSlotProps extends HTMLAttributes<HTMLDivElement> {
  index: number;
}

/** Slot state comes from {@link OTPInputContext} (not React props). */
const InputOTPSlot = forwardRef<HTMLDivElement, InputOTPSlotProps>(
  ({ index, className, ...props }, ref) => {
    const { slots } = useContext(OTPInputContext);
    const slot: SlotProps | undefined = slots?.[index];
    const char = slot?.char ?? null;
    const hasFakeCaret = slot?.hasFakeCaret ?? false;
    const isActive = slot?.isActive ?? false;
    const placeholderChar = slot?.placeholderChar ?? null;

    return (
      <div
        ref={ref}
        data-slot="input-otp-slot"
        className={cn(
          'relative flex size-9 items-center justify-center border border-border bg-background text-sm font-medium text-foreground shadow-2xs transition-all',
          'first:rounded-l-md last:rounded-r-md',
          isActive && 'z-10 ring-2 ring-primary/40',
          className
        )}
        {...props}
      >
        {char != null ? (
          char
        ) : hasFakeCaret ? (
          <OTPCaret />
        ) : placeholderChar != null ? (
          <span className="text-muted-foreground">{placeholderChar}</span>
        ) : null}
      </div>
    );
  }
);
InputOTPSlot.displayName = 'InputOTPSlot';

function OTPCaret() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
    </div>
  );
}

export interface InputOTPSeparatorProps extends HTMLAttributes<HTMLDivElement> {}

const InputOTPSeparator = forwardRef<HTMLDivElement, InputOTPSeparatorProps>(
  ({ ...props }, ref) => (
    <div ref={ref} data-slot="input-otp-separator" role="separator" {...props}>
      <span className="text-muted-foreground">-</span>
    </div>
  )
);
InputOTPSeparator.displayName = 'InputOTPSeparator';

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
